import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { LogIn, Mail, Lock, CreditCard, Check, Eye, EyeOff } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Login = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [loginData, setLoginData] = useState({
    email: '',
    password: ''
  });
  const [loginError, setLoginError] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const [showLoginPassword, setShowLoginPassword] = useState(false);

  const handleLoginChange = (e) => {
    setLoginData({
      ...loginData,
      [e.target.name]: e.target.value
    });
    setLoginError('');
  };

  const handlePostAuthRedirect = async () => {
    const sessionToken = localStorage.getItem('session_token');
    const userResponse = await axios.get(`${BACKEND_URL}/api/auth/me`, {
      headers: { Authorization: `Bearer ${sessionToken}` }
    });

    if (userResponse.data.subscription_status === 'inactive') {
      navigate('/subscribe');
    } else {
      navigate('/');
    }
  };

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    setLoginError('');

    try {
      const result = await login(loginData.email, loginData.password);

      if (result.success) {
        await handlePostAuthRedirect();
      } else {
        setLoginError(result.error);
      }
    } catch (err) {
      setLoginError('An unexpected error occurred');
    } finally {
      setLoginLoading(false);
    }
  };

  const handleSubscribeNavigation = () => {
    navigate('/subscribe');
  };

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/auth-callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const features = [
    'Unlimited quotes per month',
    'Professional PDF generation',
    'Custom business branding with logo',
    'Customizable quote numbering',
    'Quote history and tracking',
    'Email quotes to customers',
    'Mobile-friendly interface',
    'Secure cloud storage'
  ];

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: '#f5f5f5',
      padding: '2rem'
    }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem', width: '100%', maxWidth: '560px' }}>
        <div style={{
          backgroundColor: '#ffffff',
          border: '1px solid #93c5fd',
          borderRadius: '12px',
          padding: '3rem',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
          width: '100%'
        }}>
          <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
            <h1 style={{ fontSize: '2.5rem', color: '#1f2937', marginBottom: '0.5rem' }}>QuotePro</h1>
            <p style={{ fontSize: '1.1rem', color: '#475569' }}>Sign in to your account</p>
          </div>

          {loginError && (
            <div style={{
              padding: '1rem',
              backgroundColor: '#fee',
              color: '#c33',
              borderRadius: '8px',
              marginBottom: '1.5rem',
              fontSize: '1rem'
            }}>
              {loginError}
            </div>
          )}

          <form onSubmit={handleLoginSubmit}>
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{
                display: 'block',
                fontSize: '1.1rem',
                fontWeight: '600',
                color: '#1f2937',
                marginBottom: '0.5rem'
              }}>
                <Mail size={18} style={{ verticalAlign: 'middle', marginRight: '0.5rem' }} />
                Email Address
              </label>
              <input
                type="email"
                name="email"
                value={loginData.email}
                onChange={handleLoginChange}
                required
                style={{
                  width: '100%',
                  padding: '1rem',
                  fontSize: '1.1rem',
                  border: '2px solid #cbd5f5',
                  borderRadius: '8px',
                  backgroundColor: '#fff'
                }}
                placeholder="your@email.com"
              />
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{
                display: 'block',
                fontSize: '1.1rem',
                fontWeight: '600',
                color: '#1f2937',
                marginBottom: '0.5rem'
              }}>
                <Lock size={18} style={{ verticalAlign: 'middle', marginRight: '0.5rem' }} />
                Password
              </label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showLoginPassword ? 'text' : 'password'}
                  name="password"
                  value={loginData.password}
                  onChange={handleLoginChange}
                  required
                  style={{
                    width: '100%',
                    padding: '1rem',
                    paddingRight: '3rem',
                    fontSize: '1.1rem',
                    border: '2px solid #cbd5f5',
                    borderRadius: '8px',
                    backgroundColor: '#fff'
                  }}
                  placeholder="••••••••"
                  minLength="6"
                />
                <button
                  type="button"
                  onClick={() => setShowLoginPassword((prev) => !prev)}
                  aria-label={showLoginPassword ? 'Hide password' : 'Show password'}
                  style={{
                    position: 'absolute',
                    top: '50%',
                    right: '0.9rem',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: '#64748b'
                  }}
                >
                  {showLoginPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              <div style={{ textAlign: 'right', marginTop: '0.5rem' }}>
                <Link
                  to="/forgot-password"
                  style={{
                    color: '#2563eb',
                    fontSize: '0.95rem',
                    textDecoration: 'none'
                  }}
                >
                  Forgot password?
                </Link>
              </div>
            </div>

            <button
              type="submit"
              disabled={loginLoading}
              style={{
                width: '100%',
                padding: '1.25rem',
                fontSize: '1.2rem',
                fontWeight: '600',
                color: '#fff',
                backgroundColor: loginLoading ? '#93c5fd' : '#3b82f6',
                border: 'none',
                borderRadius: '10px',
                cursor: loginLoading ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem',
                marginBottom: '1rem'
              }}
            >
              <LogIn size={24} />
              {loginLoading ? 'Please wait...' : 'Sign In'}
            </button>
          </form>

          <div style={{
            position: 'relative',
            textAlign: 'center',
            margin: '1.5rem 0'
          }}>
            <span style={{
              backgroundColor: '#fff',
              padding: '0 1rem',
              color: '#94a3b8',
              fontSize: '1rem',
              position: 'relative',
              zIndex: 1
            }}>or</span>
            <div style={{
              position: 'absolute',
              top: '50%',
              left: 0,
              right: 0,
              height: '1px',
              backgroundColor: '#e2e8f0',
              zIndex: 0
            }} />
          </div>

          <button
            onClick={handleGoogleLogin}
            disabled={loginLoading}
            style={{
              width: '100%',
              padding: '1.25rem',
              fontSize: '1.2rem',
              fontWeight: '600',
              color: '#1f2937',
              backgroundColor: '#fff',
              border: '2px solid #dbeafe',
              borderRadius: '10px',
              cursor: loginLoading ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.5rem'
            }}
          >
            <svg width="24" height="24" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Continue with Google
          </button>
        </div>

        <div style={{
          backgroundColor: '#ffffff',
          border: '1px solid #93c5fd',
          borderRadius: '16px',
          padding: '2.75rem',
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
          width: '100%'
        }}>
          <div style={{ marginBottom: '1.75rem', textAlign: 'center' }}>
            <CreditCard size={56} style={{ color: '#4A90E2', margin: '0 auto 1rem' }} />
            <h2 style={{ fontSize: '2rem', marginBottom: '0.4rem', color: '#1f2937' }}>
              QuotePro Monthly
            </h2>
            <p style={{ fontSize: '1.05rem', color: '#64748b' }}>
              Professional quote management for your business
            </p>
          </div>

          <div style={{
            backgroundColor: '#f8fafc',
            borderRadius: '12px',
            padding: '2rem',
            marginBottom: '1.75rem',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '1rem', color: '#64748b', marginBottom: '0.5rem' }}>
              Only
            </div>
            <div style={{ fontSize: '3.75rem', fontWeight: '700', color: '#3b82f6', lineHeight: '1' }}>
              $19<span style={{ fontSize: '2rem' }}>.99</span>
            </div>
            <div style={{ fontSize: '1.1rem', color: '#64748b', marginTop: '0.5rem' }}>
              CAD per month
            </div>
          </div>

          <div style={{ textAlign: 'left', marginBottom: '2rem' }}>
            <h3 style={{ fontSize: '1.25rem', marginBottom: '1rem', color: '#1f2937' }}>
              What's Included:
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {features.map((feature, index) => (
                <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <div style={{
                    width: '24px',
                    height: '24px',
                    borderRadius: '50%',
                    backgroundColor: '#dbeafe',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}>
                    <Check size={16} style={{ color: '#1d4ed8' }} />
                  </div>
                  <span style={{ fontSize: '1rem', color: '#475569' }}>{feature}</span>
                </div>
              ))}
            </div>
          </div>

          <button
            onClick={handleSubscribeNavigation}
            style={{
              width: '100%',
              padding: '1.35rem',
              fontSize: '1.2rem',
              fontWeight: '600',
              color: 'white',
              backgroundColor: '#4A90E2',
              border: 'none',
              borderRadius: '12px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.75rem'
            }}
          >
            <CreditCard size={26} />
            Subscribe Now
          </button>
        </div>
      </div>
    </div>
  );
};

export default Login;
